import { createSlice } from '@reduxjs/toolkit';

import { RejectedActionFromAsyncThunk } from '@/redux/createAsyncThunk';
import { LOG_OUT_BIDDER, LogOutBidderSuccessAction } from '@/redux/modules/account/logout/logout.actions';
import {
    addBidderCreditCard,
    addBidderZipCreditCard,
    clearCreditCardErrors,
    fetchBidderCreditCards,
    updateBidderCreditCard,
} from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.actions';
import {
    CreditCardSlice,
    defaultCreditCardSlice,
} from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.types';

import { DISMISS_MODAL, DismissModalAction } from '../../../../actions';

const enterErrorStateReducer = (
    slice: CreditCardSlice,
    {
        error,
    }: RejectedActionFromAsyncThunk<
        | typeof fetchBidderCreditCards
        | typeof addBidderCreditCard
        | typeof updateBidderCreditCard
        | typeof addBidderZipCreditCard
    >
) => {
    slice.addSubmitted = false;
    slice.addSuccess = false;
    slice.error = error.message;
    slice.loading = false;
    slice.updateSubmitted = false;
};

const creditCardSlice = createSlice({
    extraReducers: (builder) => {
        builder.addCase(fetchBidderCreditCards.pending, (slice) => {
            slice.loaded = 0;
            slice.loading = true;
        });
        builder.addCase(fetchBidderCreditCards.fulfilled, (slice, { payload }) => {
            if (!payload) {
                return;
            }
            slice.allowedToAddCards = payload.allowedToAddCards;
            slice.error = '';
            slice.laPaymentCards = payload.cards;
            slice.loaded = Date.now();
            slice.loading = false;
        });
        builder.addCase(fetchBidderCreditCards.rejected, enterErrorStateReducer);

        builder.addCase(addBidderCreditCard.pending, (slice) => {
            slice.addSubmitted = true;
            slice.addSuccess = false;
            slice.error = '';
            slice.loading = false;
        });
        builder.addCase(addBidderCreditCard.fulfilled, (slice) => {
            slice.addSubmitted = false;
            slice.addSuccess = true;
            slice.loaded = Date.now();
        });
        builder.addCase(addBidderCreditCard.rejected, enterErrorStateReducer);

        builder.addCase(updateBidderCreditCard.pending, (slice) => {
            slice.error = '';
            slice.updateSubmitted = true;
            slice.updateSuccess = false;
        });
        builder.addCase(updateBidderCreditCard.fulfilled, (slice) => {
            slice.error = '';
            slice.loaded = Date.now();
            slice.updateSubmitted = false;
            slice.updateSuccess = true;
        });
        builder.addCase(updateBidderCreditCard.rejected, enterErrorStateReducer);

        builder.addCase(addBidderZipCreditCard.pending, (slice) => {
            slice.addSubmitted = true;
            slice.addSuccess = false;
            slice.error = '';
        });
        builder.addCase(addBidderZipCreditCard.fulfilled, (slice, { payload }) => {
            slice.addSubmitted = false;
            slice.addSuccess = true;
            slice.zipCard = payload;
        });
        builder.addCase(addBidderZipCreditCard.rejected, enterErrorStateReducer);

        builder.addCase(clearCreditCardErrors.fulfilled, (slice) => {
            slice.error = '';
        });

        builder.addCase<typeof DISMISS_MODAL, DismissModalAction>(DISMISS_MODAL, (slice) => {
            slice.addSubmitted = false;
            slice.addSuccess = false;
            slice.error = '';
            slice.loading = false;
        });

        builder.addCase<typeof LOG_OUT_BIDDER, LogOutBidderSuccessAction>(LOG_OUT_BIDDER, () => defaultCreditCardSlice);
    },
    initialState: defaultCreditCardSlice,
    name: 'creditCard',
    reducers: {},
});

export const { reducer: creditCardReducer } = creditCardSlice;
